import React, { useState } from 'react';
import './FunctionInput.css';

const FUNCTION_TEMPLATES = [
  { label: 'Linear: mx + b', template: '2*x + 1' },
  { label: 'Quadratic', template: 'x^2' },
  { label: 'Cubic', template: 'x^3' },
  { label: 'Sine', template: 'sin(x)' },
  { label: 'Cosine', template: 'cos(x)' },
  { label: 'Tangent', template: 'tan(x)' },
  { label: 'Cosecant', template: '1/sin(x)' },
  { label: 'Secant', template: '1/cos(x)' },
  { label: 'Cotangent', template: '1/tan(x)' },
  { label: 'Exponential', template: 'exp(x)' },
  { label: 'Natural Log', template: 'log(x)' },
  { label: 'Square Root', template: 'sqrt(x)' },
  { label: 'Absolute Value', template: 'abs(x)' },
  { label: 'Reciprocal', template: '1/x' },
];

const EXPRESSION_LIBRARY = [
  {
    category: 'Polynomials & powers',
    items: [
      { label: 'Linear', template: '2*x + 1' },
      { label: 'Quadratic', template: 'x^2' },
      { label: 'Cubic', template: 'x^3' },
      { label: 'Quartic', template: 'x^4 - 3*x^2 + 2' },
      { label: 'Power function', template: 'x^3.5' },
    ],
  },
  {
    category: 'Trigonometric',
    items: [
      { label: 'Sine', template: 'sin(x)' },
      { label: 'Cosine', template: 'cos(x)' },
      { label: 'Tangent', template: 'tan(x)' },
      { label: 'Cosecant', template: '1/sin(x)' },
      { label: 'Secant', template: '1/cos(x)' },
      { label: 'Cotangent', template: '1/tan(x)' },
    ],
  },
  {
    category: 'Exponential & logarithmic',
    items: [
      { label: 'Exponential', template: 'exp(x)' },
      { label: 'Natural logarithm', template: 'log(x)' },
      { label: 'Log base 10', template: 'log10(x)' },
    ],
  },
  {
    category: 'Algebraic & absolute',
    items: [
      { label: 'Absolute value', template: 'abs(x)' },
      { label: 'Reciprocal', template: '1/x' },
      { label: 'Square root', template: 'sqrt(x)' },
      { label: 'Floor', template: 'floor(x)' },
      { label: 'Ceil', template: 'ceil(x)' },
    ],
  },
  {
    category: 'Composite examples',
    items: [
      { label: 'Sine of square', template: 'sin(x^2)' },
      { label: 'Damped exponential', template: 'exp(-x^2)' },
      { label: 'Rational', template: '(x^2 - 1)/(x - 1)' },
      { label: 'Absolute root', template: 'sqrt(abs(x))' },
      { label: 'Sin over x', template: 'sin(x)/x' },
    ],
  },
];

function FunctionInput({ onAddFunction }) {
  const [expression, setExpression] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('#667eea');
  const [showLibrary, setShowLibrary] = useState(false);

  const handleAdd = () => {
    if (expression.trim()) {
      onAddFunction({
        expression,
        label: label || expression,
        color,
      });
      setExpression('');
      setLabel('');
      setColor('#667eea');
    }
  };

  const handleTemplate = (template) => {
    setExpression(template);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  const openLibrary = () => setShowLibrary(true);
  const closeLibrary = () => setShowLibrary(false);
  const handleSelectLibrary = (template) => {
    setExpression(template);
    setShowLibrary(false);
  };

  return (
    <div className="function-input">
      <div className="input-group">
        <label htmlFor="expression">Function Expression </label>
        <input
          id="expression"
          type="text"
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g., sin(x), x^2 + 3*x - 1"
        />
        <small className="hint">
          Supports: +, -, *, /, ^, sin, cos, tan, exp, log, sqrt, abs, floor, ceil, etc.
        </small>
      </div>

      <div className="input-group">
        <label htmlFor="label">Label (optional)</label>
        <input
          id="label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g., My Function"
        />
      </div>

      <div className="input-group">
        <label htmlFor="color">Color</label>
        <input
          id="color"
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
        />
      </div>

      <button className="add-btn" onClick={handleAdd}>
        Add Function
      </button>

      <button className="library-btn" type="button" onClick={openLibrary}>
        Browse expression library
      </button>

      <div className="templates">
        <p className="templates-title">Templates:</p>
        <div className="template-grid">
          {FUNCTION_TEMPLATES.map((template) => (
            <button
              key={template.template}
              className="template-btn"
              onClick={() => handleTemplate(template.template)}
              title={template.template}
            >
              {template.label}
            </button>
          ))}
        </div>
      </div>

      {showLibrary && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-panel">
            <div className="modal-header">
              <div>
                <h3>Plotable Expression Library</h3>
                <p>Click one to insert it into the function input.</p>
              </div>
              <button className="modal-close" onClick={closeLibrary} aria-label="Close library">
                ×
              </button>
            </div>
            <div className="library-content">
              {EXPRESSION_LIBRARY.map((group) => (
                <div key={group.category} className="library-group">
                  <h4>{group.category}</h4>
                  <div className="library-grid">
                    {group.items.map((item) => (
                      <button
                        key={item.template}
                        className="library-item"
                        type="button"
                        onClick={() => handleSelectLibrary(item.template)}
                        title={item.template}
                      >
                        <strong>{item.label}</strong>
                        <span>{item.template}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FunctionInput;
